// <s-kickoff-countdown> counts down to the next weekly kickoff in a fixed time zone and shows a live message during the game window.
// Required markup: data-day (0 = Sunday), data-hour, data-minute, data-duration (minutes), data-tz, [data-unit="days|hours|minutes|seconds"],
// [data-countdown-clock] and [data-countdown-live] (hidden by default).
const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function zonedParts(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = {};
  for (const part of formatter.formatToParts(date)) parts[part.type] = part.value;
  return parts;
}

function offsetMs(date, timeZone) {
  const p = zonedParts(date, timeZone);
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

function zonedToEpoch(year, monthIndex, day, hour, minute, timeZone) {
  const guess = Date.UTC(year, monthIndex, day, hour, minute);
  const first = guess - offsetMs(new Date(guess), timeZone);
  return guess - offsetMs(new Date(first), timeZone);
}

class SKickoffCountdown extends HTMLElement {
  #timer;

  connectedCallback() {
    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #nextKickoff(now) {
    const tz = this.dataset.tz || 'America/Chicago';
    const day = Number(this.dataset.day ?? 0);
    const hour = Number(this.dataset.hour ?? 12);
    const minute = Number(this.dataset.minute ?? 0);
    const durationMs = Number(this.dataset.duration ?? 210) * 60000;
    const p = zonedParts(now, tz);
    const ahead = (day - WEEKDAYS[p.weekday] + 7) % 7;
    let start = zonedToEpoch(Number(p.year), Number(p.month) - 1, Number(p.day) + ahead, hour, minute, tz);
    if (start + durationMs <= now.getTime()) {
      start = zonedToEpoch(Number(p.year), Number(p.month) - 1, Number(p.day) + ahead + 7, hour, minute, tz);
    }
    return { start, end: start + durationMs };
  }

  #tick() {
    const now = new Date();
    const { start, end } = this.#nextKickoff(now);
    const live = now.getTime() >= start && now.getTime() < end;
    const clock = this.querySelector('[data-countdown-clock]');
    const liveMessage = this.querySelector('[data-countdown-live]');
    if (clock) clock.hidden = live;
    if (liveMessage) liveMessage.hidden = !live;
    if (live) return;

    let remaining = Math.max(0, Math.floor((start - now.getTime()) / 1000));
    const values = {
      days: Math.floor(remaining / 86400),
      hours: Math.floor((remaining %= 86400) / 3600),
      minutes: Math.floor((remaining %= 3600) / 60),
      seconds: remaining % 60,
    };
    for (const [unit, value] of Object.entries(values)) {
      const node = this.querySelector(`[data-unit="${unit}"]`);
      if (node) node.textContent = String(value).padStart(2, '0');
    }
  }
}

if (!customElements.get('s-kickoff-countdown')) customElements.define('s-kickoff-countdown', SKickoffCountdown);
